import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useUser } from './UserContext';

export type Role = 'admin' | 'ops' | 'compliance' | 'finance' | 'sales' | 'agent' | 'customer' | 'supplier';

// Actions-first permissions
export type Action =
  | 'dashboard.view'
  | 'dashboard.viewLimited'
  | 'order.create'
  | 'order.edit'
  | 'order.view'
  | 'scheduling.full'
  | 'scheduling.view'
  | 'shipment.view'
  | 'shipment.edit'
  | 'notes.internal'
  | 'notes.finance'
  | 'notes.customer'
  | 'customs.view'
  | 'customs.respond'
  | 'customs.file'
  | 'payments.view'
  | 'payments.pay'
  | 'payments.approve'
  | 'recon.view'
  | 'recon.manage'
  | 'documents.view'
  | 'documents.upload'
  | 'documents.replace';

type PermissionMatrix = Record<Role, Set<Action>>;

// Fallback permissions - used while loading or if API fails
const FALLBACK_PERMISSIONS: PermissionMatrix = {
  admin: new Set<Action>([
    'dashboard.view',
    'order.create', 'order.edit', 'order.view',
    'scheduling.full',
    'shipment.view', 'shipment.edit', 'notes.internal',
    'customs.view', 'customs.respond', 'customs.file',
    'payments.view', 'payments.pay', 'payments.approve',
    'recon.view',
    'documents.view', 'documents.upload', 'documents.replace',
  ]),
  ops: new Set<Action>([
    'dashboard.view',
    'order.view',
    'scheduling.full',
    'shipment.view', 'shipment.edit', 'notes.internal',
    'customs.view',
    'payments.view',
    'recon.view',
    'documents.view', 'documents.upload',
  ]),
  compliance: new Set<Action>([
    'dashboard.view',
    'order.view',
    'scheduling.view',
    'shipment.view', 'notes.internal',
    'customs.view', 'customs.respond', 'customs.file',
    'payments.view',
    'documents.view', 'documents.upload', 'documents.replace',
  ]),
  finance: new Set<Action>([
    'dashboard.view',
    'order.view',
    'scheduling.view',
    'shipment.view', 'notes.finance',
    'customs.view',
    'payments.view', 'payments.pay', 'payments.approve',
    'recon.view', 'recon.manage',
    'documents.view',
  ]),
  sales: new Set<Action>([
    'dashboard.view',
    'order.create', 'order.edit', 'order.view',
    'scheduling.view',
    'shipment.view',
    'customs.view',
    'payments.view',
    'recon.view',
    'documents.view',
  ]),
  agent: new Set<Action>([
    'dashboard.view',
    'order.view',
    'scheduling.view',
    'shipment.view', 'notes.internal',
    'customs.view',
    'payments.view', 'payments.pay',
    'documents.view',
  ]),
  customer: new Set<Action>([
    'dashboard.viewLimited',
    'order.view',
    'scheduling.view',
    'shipment.view', 'notes.customer',
    'customs.view',
    'payments.view', 'payments.pay',
    'documents.view',
  ]),
  supplier: new Set<Action>([
    'dashboard.viewLimited',
    'order.view',
    'shipment.view',
    'customs.view',
    'documents.view',
    'payments.view',
  ]),
};

// GraphQL query to fetch role permissions from database
const ADMIN_ACCESS_QUERY = gql`
  query AdminAccessSnapshot {
    adminAccessSnapshot(limit: 100, offset: 0, auditLimit: 0) {
      roles {
        role
        label
        authorityLevel
        scope
        intent
        userCount
        primaryScreens
        scopeLocked
        permissions {
          screen
          access
          matrixCode
          restrictionReason
          capabilities {
            icon
            text
          }
        }
      }
    }
  }
`;

// Map screen name + access level to Action set
function mapPermissionsToActions(role: Role, permissions: Array<{ screen: string; access: string; matrixCode: string }>): Set<Action> {
  const actions = new Set<Action>();

  for (const perm of permissions) {
    const { screen, access, matrixCode } = perm;

    // Skip restricted access entirely
    if (access === 'restricted') {
      continue;
    }

    // Dashboard
    if (screen === 'Dashboard') {
      if (access === 'full' || access === 'view-only') {
        if (role === 'customer' || role === 'supplier') {
          actions.add('dashboard.viewLimited');
        } else {
          actions.add('dashboard.view');
        }
      }
    }

    // Create Order
    if (screen === 'Create Order') {
      if (access === 'full') {
        actions.add('order.create');
        actions.add('order.edit');
        actions.add('order.view');
      } else if (access === 'view-only') {
        actions.add('order.view');
      }
    }

    // Scheduling
    if (screen === 'Scheduling') {
      if (access === 'full') {
        actions.add('scheduling.full');
      } else if (access === 'view-only') {
        actions.add('scheduling.view');
      }
    }

    // Shipments
    if (screen === 'Shipments') {
      if (access === 'full') {
        actions.add('shipment.view');
        actions.add('shipment.edit');
        // Add notes.internal for ops/admin editing shipments
        if (role === 'ops' || role === 'admin') {
          actions.add('notes.internal');
        }
      } else if (access === 'view-only') {
        actions.add('shipment.view');
      }
    }

    // Customs
    if (screen === 'Customs') {
      if (access === 'full') {
        actions.add('customs.view');
        actions.add('customs.respond');
        actions.add('customs.file');
      } else if (access === 'view-only') {
        actions.add('customs.view');
      }
    }

    // Payments
    if (screen === 'Payments') {
      if (access === 'full') {
        actions.add('payments.view');
        actions.add('payments.pay');
        actions.add('payments.approve');
      } else if (matrixCode === 'Pay') {
        // Special case: agent and customer can only pay
        actions.add('payments.view');
        actions.add('payments.pay');
      } else if (access === 'view-only') {
        actions.add('payments.view');
      }
    }

    // Reconciliation
    if (screen === 'Reconciliation') {
      if (access === 'full') {
        actions.add('recon.view');
        actions.add('recon.manage');
      } else if (access === 'view-only') {
        actions.add('recon.view');
      }
    }

    // Documents
    if (screen === 'Documents') {
      if (access === 'full') {
        actions.add('documents.view');
        actions.add('documents.upload');
        actions.add('documents.replace');
      } else if (access === 'view-only') {
        actions.add('documents.view');
      }
    }

    // Notes: Special handling based on context
    if ((screen === 'Shipments' || screen === 'Create Order') && access === 'full' && !actions.has('notes.internal')) {
      // Full access to shipments/orders implies internal notes
      actions.add('notes.internal');
    }
    if (role === 'finance' && screen === 'Payments' && access === 'full') {
      actions.add('notes.finance');
    }
    if ((role === 'customer' || role === 'supplier') && access === 'view-only') {
      actions.add('notes.customer');
    }
  }

  return actions;
}

const roleDisplayNames: Record<Role, string> = {
  admin: 'Admin – Business Owner',
  ops: 'Ops – Logistics / Execution',
  compliance: 'Compliance – Customs & Docs',
  finance: 'Finance – Payments & Invoices',
  sales: 'Sales – Front-office',
  agent: 'Agent – Tvikla Authorized',
  customer: 'Customer – Buyer',
  supplier: 'Supplier – Seller/Manufacturer',
};

function normalizeRoleValue(roleValue?: string | null): Role | null {
  if (!roleValue) {
    return null;
  }

  const value = roleValue.trim().toLowerCase();
  if (isValidRole(value)) {
    return value;
  }

  if (value.includes('admin')) return 'admin';
  if (value.includes('compliance')) return 'compliance';
  if (value.includes('finance')) return 'finance';
  if (value.includes('sales') || value.includes('seller')) return 'sales';
  if (value.includes('agent')) return 'agent';
  if (value.includes('customer') || value.includes('buyer')) return 'customer';
  if (value.includes('supplier') || value.includes('manufacturer') || value.includes('vendor')) return 'supplier';
  if (value.includes('ops') || value.includes('logistics') || value.includes('operations')) return 'ops';

  return null;
}

interface RoleContextType {
  role: Role;
  can: (action: Action) => boolean;
  switchRole: (next: Role) => void;
  roleLabel: string;
  loading: boolean;
  error?: string;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [role, setRole] = useState<Role>('admin');
  const [permissions, setPermissions] = useState<PermissionMatrix>(FALLBACK_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const { user } = useUser();

  // Fetch role permissions from GraphQL API
  const { data, loading: queryLoading, error: queryError } = useQuery(ADMIN_ACCESS_QUERY, {
    fetchPolicy: 'cache-first',
  });

  // Initialize role from session storage
  useEffect(() => {
    const saved = sessionStorage.getItem('currentRole') as Role | null;
    if (saved && isValidRole(saved)) {
      setRole(saved);
    }
  }, []);

  useEffect(() => {
    const nextRole = normalizeRoleValue(user?.role);
    if (!nextRole) {
      return;
    }

    if (nextRole !== role) {
      setRole(nextRole);
      sessionStorage.setItem('currentRole', nextRole);
    }
  }, [role, user?.role]);

  // Update permissions when API data arrives
  useEffect(() => {
    if (queryLoading) {
      setLoading(true);
      return;
    }

    if (queryError) {
      console.warn('Failed to fetch role permissions from API, using fallback:', queryError);
      setError('Using fallback permissions');
      setPermissions(FALLBACK_PERMISSIONS);
      setLoading(false);
      return;
    }

    if (data?.adminAccessSnapshot?.roles) {
      const newPermissions = buildPermissionMatrix(data.adminAccessSnapshot.roles);
      setPermissions(newPermissions);
      setError(undefined);
    } else {
      setPermissions(FALLBACK_PERMISSIONS);
    }

    setLoading(false);
  }, [data, queryLoading, queryError]);

  const can = useMemo(() => (action: Action) => permissions[role]?.has(action) ?? false, [role, permissions]);

  const switchRole = (next: Role) => {
    if (isValidRole(next)) {
      setRole(next);
      sessionStorage.setItem('currentRole', next);
    }
  };

  const value: RoleContextType = {
    role,
    can,
    switchRole,
    roleLabel: roleDisplayNames[role],
    loading,
    error,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = () => {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within RoleProvider');
  return ctx;
};

// Helper: validate role type
function isValidRole(role: string): role is Role {
  return ['admin', 'ops', 'compliance', 'finance', 'sales', 'agent', 'customer', 'supplier'].includes(role);
}

// Helper: Build PermissionMatrix from API role data
function buildPermissionMatrix(roles: Array<{ role: string; permissions: Array<{ screen: string; access: string; matrixCode: string }> }>): PermissionMatrix {
  const matrix: PermissionMatrix = { ...FALLBACK_PERMISSIONS };

  for (const roleData of roles) {
    if (isValidRole(roleData.role)) {
      matrix[roleData.role] = mapPermissionsToActions(roleData.role, roleData.permissions);
    }
  }

  return matrix;
}
